import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationChangeType, IntegrationItemServiceInf, SpecificIntegrationActionType, StatusString } from '../interfaces/integrations';
import { AdminService } from 'src/app/core/services/admin.service';
import { IntegPolicyService } from 'src/app/core/services/integ-policy.service';
import { Account, WorkerPostRequest } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { SpinnerService } from './spinner.service';
import { WorkflowService } from './workflow.service';
import { MatDialog } from '@angular/material/dialog';
import { PresenceMonitorConfigDialogComponent } from 'src/app/modules/integrations-applications/components/integ-config-dialogs/presence-monitor-config-dialog/presence-monitor-config-dialog.component';
import { PresenceMonitorState, PresenceMonitor, PresenceUpdateReason } from '../interfaces/msteams-presence-data';
import { MSTeamsSharedService } from './msteams-shared.service';
import { TranslateService } from '@ngx-translate/core';
import { Util } from '../utils/Util';
import { FeatureFlags } from '../interfaces/feature-flags';
import { FeatureToggleService } from './feature-toggle.service';

const logContext = 'IntegMSTeamsToCLPresenceService';
@Injectable({
	providedIn: 'root'
})

export class IntegMSTeamsToCLPresenceService implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private policyService: IntegPolicyService;
	private accountsService: AccountsService;
	private spinnerService: SpinnerService;
	private workflowService: WorkflowService;
	private msteamsSharedService: MSTeamsSharedService;
	public dialog: MatDialog;
	private translateService: TranslateService;
	private featureToggleService: FeatureToggleService = undefined;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.workflowService )
			this.workflowService = AppInjector.injector.get(WorkflowService);

		if ( !this.msteamsSharedService )
			this.msteamsSharedService = AppInjector.injector.get(MSTeamsSharedService);

		if ( !this.translateService )
			this.translateService = AppInjector.injector.get(TranslateService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if (!this.featureToggleService)
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);
	}
	
	canBeSelected(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}
	
	isIntegrationStateChangeable(account: Account): Promise<boolean>
	{
		return Promise.resolve(true);
	}
	
	isIntegrationStateOn(account: Account): Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeamsToCloudLink )
			{
				if ( tags.MSTeamsToCloudLink.enabled == true )
					return true;
				return false;
			}
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}

	getConfigurationDialog() : any
	{
		return PresenceMonitorConfigDialogComponent;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeamsToCloudLink )
			{
				if ( tags.MSTeamsToCloudLink.setupComplete == true )
					return true;
				return false;
			}
			else
				return false;
		})
		.catch(err => {
			return false;
		})
	}

	// may need some more involved checking
	canConfigure( account: Account ) : Promise<boolean>
	{
		return Promise.resolve(true);
	}

	// purpose of this method is to let the base child integration know if it should notify the parent it changed
	// We don't want that to be automatic because some of the subfeatures are benign and would not
	// change the current view of the accounts app so we do not want a refresh on every toggle
	// maybe in the future if we refactor some of the integration framework, this could be handled in the 
	// stage change method? More analysis required
	shouldNotifyParentOfChange( account: Account, changeType: IntegrationChangeType ): Promise<any>
	{
		// any change to this subfeature may cause a error status change
		if ( changeType == IntegrationChangeType.DIALOG_CLOSE )
		{
			return Promise.resolve({product: this, reason: "refreshOwnIntegration", actionType: SpecificIntegrationActionType.REFRESH_ONLY});
		}
		else if ( changeType == IntegrationChangeType.ENABLED_STATE )
		{
			return Promise.resolve({product: this, reason: "refreshOwnIntegration", actionType: SpecificIntegrationActionType.ENABLE_AND_REFRESH});
		}
		else if ( changeType == IntegrationChangeType.DISABLED_STATE )
		{
			return Promise.resolve({product: this, reason: "refreshOwnIntegration", actionType: SpecificIntegrationActionType.DISABLE_AND_REFRESH});
		}
		return Promise.resolve(undefined);
	}

	canChangeIntegrationState(account: Account, state: boolean): Promise<boolean>
	{
		this.initServices();
		return this.msteamsSharedService.confirmPresenceFeatureStateChange( account, state );
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();

		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();
		this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache(); // any state change can cause the status string to be out of date
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			console.debug(`${logContext}.handleIntegrationStateChange the account before changes `, newAccount);
			if ( state == true )
			{
				// workflows are suppose to exist at this point so we will enable the 3 that needs to be enabled

				if ( newAccount.tags && newAccount.tags.MSTeamsToCloudLink && newAccount.tags.MSTeamsToCloudLink.setupComplete == true )
				{
					// turn on the MS Teams Integration 
					// we should be able to communicate with the MiCollab otherwise we would not have been able to 
					// setup the integration
					// we are absorbing any errors at this point because we really do not know what to expect
					await this.enableMSTeamsIntegration(newAccount, true);

					// this accounts presence was cloned and set up at some point
					// there is an existing cloned workflow so enable it
					return this.msteamsSharedService.enableMSTeamsToCloudLinkPresenceWorkflows( newAccount, true ).then( result => {
						this.spinnerService.hideSpinner(); 
						return result;
					});
				}
				else
				{
					this.spinnerService.hideSpinner(); 
					return;
				}
			}
			else
			{
				// here we will turn off the 3 workflows for the account
				if ( newAccount.tags && newAccount.tags.MSTeamsToCloudLink )
				{
					// turn off the MS Teams Integration 
					// we should be able to communicate with the MiCollab otherwise we would not have been able to 
					// setup the integration
					// we are absorbing any errors at this point because we really do not know what to expect
					await this.enableMSTeamsIntegration(newAccount, false);

					// before we can disable the workflows, we must notify the PBX scheduler workflow it is about to be shutdown
					await this.notifyWorkflowOfConfigurationChange( newAccount.tags, PresenceUpdateReason.DISABLE_FEATURE );

					// this accounts presence was enabled at some point
					// there is an existing cloned workflow so disable it
					return this.msteamsSharedService.enableMSTeamsToCloudLinkPresenceWorkflows( newAccount, false ).then( result => {
						this.spinnerService.hideSpinner(); 
						return result;
					});
				}
				else
				{
					this.spinnerService.hideSpinner(); 
					return;
				}
			}
		}).catch(err => {
			console.error(`${logContext}.handleIntegrationStateChange Failed to handle the state change to ${state} err= `, err);
			this.spinnerService.hideSpinner();
			throw err;
		});
	}

	// When the parent is removed, this is the method called so the child can clean up as well
	// remove the workflow configurations
	removeAsASubintegration?( account: Account ): Promise<any>
	{
		this.initServices();
		this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
		this.spinnerService.showSpinner();

		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			console.debug(`${logContext}.removeAsASubintegration the account before changes `, newAccount);

			//if the proper tags do not exist, there is nothing to remove

			this.msteamsSharedService.clearUpgradePendingCache( account );
			this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache();
			this.msteamsSharedService.resetAccountMSTeamsData();
			// turn off the MS Teams Integration 
			// we should be able to communicate with the MiCollab otherwise we would not have been able to 
			// setup the integration
			// we are absorbing any errors as we are deleting the feature
			try
			{
				await this.enableMSTeamsIntegration(newAccount, false);
			}
			catch(err)
			{
				console.error(`${logContext}.removeAsASubintegration Failed to disable the MS Teams Integration err= `, err);
			}

			if ( !newAccount.tags?.MSTeamsToCloudLink )
			{
				console.log(`${logContext}.removeAsASubintegration there is no MSTeamsToCloudLink tag so nothing to remove`);
				return;
			}
			// clean up the presence workflow and tags
			// basically need to undo all this
			// tags.MSTeamsToCloudLink: {
			// 	presenceMonitors: [
			// 		{monitorId: "monitorId", connectionId: "connectionId"}
			// 	],
			//	client: {
			//		clientId: "clientId",
			//		policyId: "policyId"
			//	},
			// 	validatorWorkflow: {
			// 		enabled: true,
			// 		workflowId: "workflowId",
			// 		workflowVersion: "version"		
			// 	},
			// 	msGraphPresenceChangeWorkflow: {
			// 		enabled: false
			// 		workflowId: "workflowId",
			// 		workflowVersion: "version"		
			// 	},
			// 	msTeamsPresenceToPBXUserChangeWorkflow: {
			// 		enabled: false
			// 		workflowId: "workflowId",
			// 		workflowVersion: "version"		
			// 	},
			// 	msTeamsPresenceToPBXSchedulerWorkflow: {
			// 		enabled: false
			// 		workflowId: "workflowId",
			// 		workflowVersion: "version"		
			// 	},
			// 	updateMiCollabPresenceWorkflow: {
			// 		enabled: false
			// 		workflowId: "workflowId",
			// 		workflowVersion: "version"		
			// 	},
			//	setupComplete: true or false

			try
			{
				await this.msteamsSharedService.removeMSTeamsToCloudLinkWorkflows( newAccount, newAccount.tags );
			}
			catch (e)
			{
				console.error(`${logContext}.removeAsASubintegration The deletion of the workflows failed, continuing with the rest of the deleting error=`, e);
			}

			// delete the presence monitors
			if ( newAccount.tags.MSTeamsToCloudLink.presenceMonitors )
			{
				for ( let monitor of newAccount.tags.MSTeamsToCloudLink.presenceMonitors )
				{
					try
					{
						await this.workflowService.deleteMSTeamsConnector( monitor.connectionId );
					}
					catch (e)
					{
						console.error(`${logContext}.removeAsASubintegration The deletion of the presence monitor failed, continuing with the rest of the deleting error=`, e);
					}
				}
			}

			try
			{
				await this.adminService.deleteAccountTag( account.accountId, 'MSTeamsToCloudLink' );
			}
			catch (e)
			{
				// delete is always best effort so it would not be a failure if something did not delete 
				// properly
				console.error(`${logContext}.removeAsASubintegration There was a delete error when removing the MSTeamsToCloudLink tag error=`, e);
			}
			
			this.spinnerService.hideSpinner();
			return;
		})
		.catch(err => {
			console.error(`${logContext}.removeAsASubintegration Failed to get the account `, err);
			this.spinnerService.hideSpinner();
			return;
		});
	}

	handleEventFromAnotherIntegration( account: Account, event: any ): Promise<any>
	{
		this.initServices();
		if ( event && (event.actionType == SpecificIntegrationActionType.ENABLE_AND_REFRESH || 
				event.actionType == SpecificIntegrationActionType.DISABLE_AND_REFRESH) )
		{
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.working');
			this.spinnerService.showSpinner();
			return this.adminService.getAccountTags( account.accountId ).then( tags => {
				// if the integration has never been setup there is nothing to do
				this.spinnerService.hideSpinner();
				if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.setupComplete == true )
				{
					if ( tags.MSTeamsToCloudLink.enabled == true && event.actionType == SpecificIntegrationActionType.DISABLE_AND_REFRESH )
					{
						// if already disabled, skip it
						return this.handleIntegrationStateChange( account, false ).then(result => {
							let event: any = {
								product: this,
								reason: "refreshOwnIntegration"
							}
							return event;
						});
					} 
					else if ( ((tags.MSTeamsToCloudLink.enabled == false) || (tags.MSTeamsToCloudLink.enabled == undefined)) 
							&& event.actionType == SpecificIntegrationActionType.ENABLE_AND_REFRESH )
					{
						// if already enabled, skip it
						return this.handleIntegrationStateChange( account, true ).then(result => {
							let event: any = {
								product: this,
								reason: "refreshOwnIntegration"
							}
							return event;
						});
					}
					else
						Promise.resolve();
				}
				else
					Promise.resolve();
			})
			.catch(err => {
				console.error(`${logContext}.handleEventFromAnotherIntegration Failed to handle request`, err);
				this.spinnerService.hideSpinner();
				throw (err);
			});
		}
		else
			Promise.resolve();
	}

	// sends a request to the MiCollab to turn on (or off if required in the future) the MS Teams integration
	// Don't care if it is already turned on as requesting its status would 
	// increase the time to enable the feature
	async enableMSTeamsIntegration( account: Account, enable: boolean ): Promise<any>
	{
		this.initServices();
		try
		{
			await this.msteamsSharedService.enableMiCollabMSTeamsIntegration(account, enable);
			// it was successful, so clear the cache
			this.msteamsSharedService.clearMSTeamsToCLPresenceStatusString();
		}
		catch (err)
		{
			console.error( `${logContext}.enableMSTeamsIntegration Enabling/ disabling the MS Teams Integration on the MiCollab failed, err:`, err);
			// here we will try to translate the error to something user consumable
			// this code section will increase as we discover more errors
			if ( err && err.statusCode == 403 )
			{
				let msg;
				if ( err && err.body && err.body.message )
					msg = err.body.message;
				else if ( err && err.message )
					msg = err.message;

				if ( msg && msg.indexOf("Either the MS Teams user profile is assigned to one or more users or status presence") > -1 )
				{
					let newErr = {
						statusCode: 403,
						message: this.translateService.instant('integrations.MSTeamsToCLPresence.micollab-error-user-profiles')
					}
					throw(newErr);
				}
			}
			// throw the exception as is
			throw(err);
		}
	}

	getMSGraphConnectorData( account: Account ): Promise<any>
	{
		this.initServices();
		// this service should not even be called for this unless the MS Graph connector is already set up.
		// only issue would come is if it was removed outside of the app
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeams && tags.MSTeams.connectionId )
			{
				return this.workflowService.getMicrosoftConnector(tags.MSTeams.connectionId).then( result => {
					console.debug(`${logContext}.getMSGraphConnectorData get the connector `, result);
					return result;
				})
			}
			else
			{
				let data: any = {
					tenantId: "",
					clientId: "",
					clientSecret: "",
					connectionId: undefined
				}
				return data;
			}
		});
	}

	async getAllMonitorData( account: Account, msGraphConnectionData: any ): Promise<PresenceMonitor[]>
	{
		let monitorList: PresenceMonitor[] = [];

		this.initServices();
		// get the account tags and extract the monitors
		return this.adminService.getAccountTags( account.accountId ).then( async tags => {
			if ( tags?.MSTeamsToCloudLink?.presenceMonitors )
			{
				// TBD should this be broken out into a separate method so that we can refresh the status only or should
				// we refresh the whole list? It will depend on the requirements I guess
				// for each monitor request the current connection status
				console.log(`${logContext}.getAllMonitorData monitor list tag `, tags.MSTeamsToCloudLink.presenceMonitors);

				for ( let monitor of tags.MSTeamsToCloudLink.presenceMonitors )
				{
					let monitorData: PresenceMonitor = {
						monitorId: monitor.monitorId,
						connectionId: monitor.connectionId,
						status: PresenceMonitorState.UNKNOWN
					};
					try 
					{
						monitorData.status = await this.workflowService.getMicrosoftConnector(monitor.connectionId).then( result => {
							console.debug(`${logContext}.getAllMonitorData getMicrosoftConnector(${monitor.connectionId}), data=`, result);
							if ( (result.tenantId != msGraphConnectionData.tenantId) || (result.clientId != msGraphConnectionData.clientId) )
							{
								// the parent connector was changed
								// this monitor might still be connected BUT it would not be able to be used in feature
								return PresenceMonitorState.NEEDS_UPDATE;
							}
							return result.connected === true ? PresenceMonitorState.AUTHORIZED : PresenceMonitorState.NOT_AUTHORIZED;
						});
						monitorList.push(monitorData);
					}
					catch( err )
					{
						console.error(`${logContext}.getAllMonitorData There was an error retrieving the connector ${monitor.connectionId} error=`, err);
						if ( err && err.statusCode == 404 )
						{
							// the connector no longer exists, we are going to remove it
							// this is not a typical event. We will remove each monitor separately since
							// this the likelyhood there is more than one removed is very slim
							// if it turns out not to be the case, we can make this an update after all the
							// monitors have been tested.
							await this.deleteConnectorData(account, monitor.connectionId);
						}
						else
						{
							monitorList.push(monitorData); // leave it at unknown state for now - workflow might give a better error/ status
						}
					}
				}
			}
			console.log(`${logContext}.getAllMonitorData Returning monitor list with connection data: `, monitorList);
			return monitorList;
		});
	}

	saveConnectorData(account: Account, connectionData: any ): Promise<any>
	{
		this.initServices();
		// here we will create the connector 
		return this.workflowService.saveMicrosoftAuthConnector( account, connectionData ).then( data => {
			console.debug(`${logContext}.saveConnectorData After the save of the connector `, data );

			if ( connectionData.connectionId == undefined ) // need to update the tags
			{
				let monitorData = {
					connectionId: data.connectionId,
					monitorId: connectionData.monitorId
				}

				return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
					let dataChanges = {
						tags: {
							MSTeamsToCloudLink: newAccount.tags.MSTeamsToCloudLink ? newAccount.tags.MSTeamsToCloudLink : { presenceMonitors: [] }
							}
						}

					if ( dataChanges.tags.MSTeamsToCloudLink.presenceMonitors == undefined )
						dataChanges.tags.MSTeamsToCloudLink.presenceMonitors = [];

					dataChanges.tags.MSTeamsToCloudLink.presenceMonitors.push(monitorData);
					dataChanges.tags.MSTeamsToCloudLink.setupComplete = true;

					// need to add the connector to the workflows that need it
					// the workflows are already cloned and configured at this point
					let connectorList = this.msteamsSharedService.tagMonitorListToConnectorList(dataChanges.tags.MSTeamsToCloudLink.presenceMonitors);
					return this.workflowService.configureWorkflowConnectorIdVariable(newAccount.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowId,
							connectorList).then( result1 => {
						return this.workflowService.configureWorkflowConnectorIdVariable(newAccount.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId,
									connectorList).then( result2 => {
							// OK here we will kick the msTeamsPresenceToPBXSchedulerWorkflow workflow to trigger the change
							// we will not wait for a result because we do not care
							this.notifyWorkflowOfConfigurationChange( newAccount.tags, PresenceUpdateReason.CONNECTION_ID_CHANGE );
							console.debug(`${logContext}.saveConnectorData about to save the integration tag changes`, dataChanges);
							// finally update the account tags with the new monitor
							return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
								this.accountsService.mergeAccountWithModel(result);
								return data;
							});
						});
					});
				});
			}
			else
			{
				// should be nothing to update in workflows or account tags
				if ( connectionData.connectionId != data.connectionId )
				{
					console.error(`${logContext}.saveConnectorData The connector id ${connectionData.connectionId} does not match the connector id ${data.connectionId} after the save`);
				}
				return data;
			}
		})
		.catch (err => {
			console.error(`${logContext}.saveConnectorData Error saving microsoft connector`, connectionData, err);
			throw err;
		})
	}

	invokeWorkflowToRefreshSubscription( account: Account ): Promise<any>
	{
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
			return this.notifyWorkflowOfConfigurationChange( newAccount.tags, PresenceUpdateReason.REFRESH );
		});
	}

	notifyWorkflowOfStatusChange( account: Account ): Promise<any>
	{
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( newAccount => {
			return this.notifyWorkflowOfConfigurationChange( newAccount.tags, PresenceUpdateReason.CONNECTION_ID_CHANGE );
		});
	}

	notifyWorkflowOfConfigurationChange( tags: any, reason?: string )
	{
		// only notify if the subfeature is enabled, otherwise it is not used
		if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.enabled == true )
		{
			let reqBody: WorkerPostRequest = {
				inputs: { reason: reason ?? PresenceUpdateReason.REFRESH }
			}
			this.workflowService.executeWorkflowWithNoWait(tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId, reqBody);
		}
	}

	getMicrosoftAuthConnectorWithLinks( connectionId: string ): Promise<any>
	{
		this.initServices();
		return this.workflowService.getMicrosoftAuthConnector( connectionId ).then( result => {
			return this.workflowService.putMicrosoftAuthConnector( result ).then( result2 => {
				return result2;
			});
		})
		.catch( err => {
			console.error(`${logContext}.getMicrosoftAuthConnectorWithLinks Error retrieving the connector error=`, err);
			throw err;
		})
	}

	async deleteConnectorData(account: Account, connectionId: any ): Promise<void>
	{
		this.initServices();
		return this.adminService.getAccountWithoutFrills(account.accountId, null, {query: {$expand: 'tags'}}).then( async newAccount => {
			let dataChanges = {
				tags: {
					MSTeamsToCloudLink: newAccount.tags.MSTeamsToCloudLink ? newAccount.tags.MSTeamsToCloudLink : { presenceMonitors: [] }
					}
				}
			if ( dataChanges.tags.MSTeamsToCloudLink.presenceMonitors == undefined )
				dataChanges.tags.MSTeamsToCloudLink.presenceMonitors = [];

			// delete is best effort. One failure should not prevent the deletion from continuing
			try
			{
				await this.workflowService.deleteMSTeamsConnector( connectionId );
			}
			catch (err)
			{
				if ( err && err.statusCode != 404 )
				{
					console.error(`${logContext}.deleteConnectorData Error deleting connector ${connectionId}, continuing with delete err = `, err);
				}
			}

			dataChanges.tags.MSTeamsToCloudLink.presenceMonitors = dataChanges.tags.MSTeamsToCloudLink.presenceMonitors.filter( m => m.connectionId != connectionId );

			// need to remove the connector to the workflows that need it
			// the workflows are already cloned and configured at this point

			let connectorList = this.msteamsSharedService.tagMonitorListToConnectorList(dataChanges.tags.MSTeamsToCloudLink.presenceMonitors);

			// remove connector from msTeamsPresenceToPBXUserChangeWorkflow
			try
			{
				let result1 =  await this.workflowService.configureWorkflowConnectorIdVariable(
									newAccount.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXUserChangeWorkflow.workflowId,
									connectorList);
			}
			catch (err)
			{
				console.error(`${logContext}.deleteConnectorData Error configuring connector variables in the msTeamsPresenceToPBXUserChangeWorkflow WF, continuing with delete error=`, err);
			}
				
			// remove connector from msTeamsPresenceToPBXSchedulerWorkflow
			try
			{
				let result2 =  await this.workflowService.configureWorkflowConnectorIdVariable(
									newAccount.tags.MSTeamsToCloudLink.msTeamsPresenceToPBXSchedulerWorkflow.workflowId,
									connectorList);
			
			}
			catch (err)
			{
				console.error(`${logContext}.deleteConnectorData Error configuring connector variables in the msTeamsPresenceToPBXSchedulerWorkflow WF, continuing with delete error=`, err);
			}

			// OK here we will kick the msTeamsPresenceToPBXSchedulerWorkflow workflow to trigger the change
			// we will not wait for a result because we do not care
			this.notifyWorkflowOfConfigurationChange( newAccount.tags, PresenceUpdateReason.CONNECTION_ID_CHANGE );
			// finally update the account tags with the new monitor
			console.debug(`${logContext}.deleteConnectorData about to save the tag changes `, dataChanges);
			return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
				this.accountsService.mergeAccountWithModel(result);
				this.spinnerService.hideSpinner();
				return;
			});
		});
	}

	setupAllPresenceWorkflows(account: Account): Promise<any>
	{
		// clone all the workflows for this feature if they don't exist yet
		// the only one that should be enabled is the connector validator WF
		// use the same client for all of them
		// Save the WF data to the accounts tags

		this.initServices();
		let msGraphConnectorId;
		// get the current account tags
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			if ( tags && tags.MSTeams && tags.MSTeams.setupComplete == true && tags.MSTeams.connectionId != undefined )
			{
				msGraphConnectorId = tags.MSTeams.connectionId;
			}
			else
			{
				let err = new Error("MS Teams integration not setup");
				throw err;
			}

			if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.validatorWorkflow != undefined )
			{
				// consider the workflows already cloned - nothing to do
				return;
			}

			return this.workflowService.getMicrosoftConnector( msGraphConnectorId ).then( connectorData => {
				if ( !connectorData || connectorData.clientId == null || connectorData.clientId.length == 0 )
				{
					// looks like the connector was removed outside the accounts app or there was an error
					// requesting it?
					let err: any = {
						body: {
							message: "missing MS teams integration"
						}
					}
					throw err;
				}
		
				return this.msteamsSharedService.createMSTeamsToCloudLinkWorkflows( account, tags ).then( dataChanges => {
					return this.adminService.updateAccount( account.accountId, dataChanges).then(result => {
						this.accountsService.mergeAccountWithModel(result);
						console.debug(`${logContext}.setupAllPresenceWorkflows Successfully setup all workflows result = `, result);
						return true;
					});
				})
			});

		})
		.catch(err => {
			console.error(`${logContext}.setupAllPresenceWorkflows Error setting up all workflows error=`, err);
			if ( err && err.statusCode == 404 && err.body && err.body.message && err.body.message == msGraphConnectorId )
            {
                // the MS teams connector is missing. Throw a specific error
                let newErr: any = {
                    body: {
                        message: "missing MS teams integration"
                    }
                }
                throw newErr;
            }
			throw err;
		});
	}

	getAccountMonitorDetails(account: Account, reset: boolean = false): Promise<any>
	{
		this.initServices();
		return this.adminService.getAccountTags( account.accountId ).then( tags => {
			return this.msteamsSharedService.getAccountMonitorDetailsFromWorkflow(account, tags, reset);
		})
		.catch(err => {
			console.error(`${logContext}.getAccountMonitorDetails Error getting account monitor details err= `, err);
			throw err;
		})
	}

	getStatusString(account: Account): Promise<StatusString>
	{

		this.initServices();
		return this.msteamsSharedService.getMSteamsToCLPresenceStatusString( account );

	}

	isCheckTagsIntegrationEnabled( tags: any )
	{
		if ( tags && tags.MSTeamsToCloudLink )
		{
			if ( tags.MSTeamsToCloudLink.enabled == true )
				return true;
			return false;
		}
		else
			return false;
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		// TBD do we want to change the button label to configure?
		return undefined;
	}

  isButtonDisabled(account: Account): Promise<boolean> {
    // requires a MiCollab that supports the feature and an onboarded MiVB
    // need to wait until apis are ready
    this.initServices();
    return this.msteamsSharedService.isMiCollabAvailable(account).then(available => {
      if (!available) {
        return true;
      }
      return this.msteamsSharedService.doesMiCollabHaveAConfigurationError(account).then(hasMiCollabConfigError => {
        if (hasMiCollabConfigError) {
          return true;
        }
        // it is unlikely we get this far
        return this.adminService.getAccountTagsNoFrills(account.accountId).then(async tags => {
          let hasMiVB = await this.msteamsSharedService.doesAccountHaveMiVoiceBusinessOnboarded(account, tags);
		  if ( !hasMiVB )
		  {
			let FFlag = this.featureToggleService.isFeatureEnabled(FeatureFlags.ALLOW_MSTEAMS_ADDITIONAL_PBXES);
			if ( FFlag )
			{
				let hasPBX = Util.doesAccountHavePBXandGatewayOnboarded(tags, ['MX-ONE','MiVoice 5000' ]);
				return !hasPBX
			}
			else
				return !hasMiVB;
		  }
		  return !hasMiVB;
        });
      });
    })
      .catch(err => {
        console.error(`${logContext}.isButtonDisabled Error determining disabled status err= `, err);
        return true; // if we can't get the information, assume disabled. 
      });
  }

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		return Promise.resolve(false); // should never be called, but just in case
	}

}
